import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import "./RecruitingDashboard.css";
import PageContent from "../shared/PageContent/PageContent";
import SetGoalsModal from "./SetGoalsModal";
import CreateListModal from "./CreateListModal";
import EditListModal from "./EditListModal";
import AddLeadModal from "./AddLeadModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import MeetingReportModal from "./MeetingReportModal";
// import {
//   getUpcomingEvents,
//   getEventsForDateRange,
//   isSignedIn,
// } from "../../services/googleCalendar";
import { useToast } from "../../context/ToastContext";
import { handleGenericError } from "../../utils/errorHandler";

function RecruitingDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getVerifiedJWT } = useAuth();
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [userGoals, setUserGoals] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showError } = useToast();
  const [dashboardData, setDashboardData] = useState({
    needsAttention: {
      noContact7Days: 0,
      offersPendingSignup: 0,
    },
    upcomingMeetings: [],
    pipelineSnapshot: {
      lead: { count: 0, goal: 0 },
      firstMeeting: { count: 0, goal: 0 },
      additional: { count: 0, goal: 0 },
      offerSent: { count: 0, goal: 0 },
      signed: { count: 0, goal: 0 },
    },
    insights: {
      conversionRate: 0,
      avgDaysPerStage: 0,
      totalPipeline: 0,
    },
  });
  const [selectedWeekStart, setSelectedWeekStart] = useState(null); // For week navigation
  const [recruitingLists, setRecruitingLists] = useState([]);
  const [isListsLoading, setIsListsLoading] = useState(true);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [listToEdit, setListToEdit] = useState(null);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [isMeetingsLoading, setIsMeetingsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedRecruitForReport, setSelectedRecruitForReport] =
    useState(null);
  const [selectedMeetingForReport, setSelectedMeetingForReport] =
    useState(null);

  // Fetch user goals
  const fetchUserGoals = async () => {
    setIsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/recruiting/goals"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setUserGoals(result.data);
      }
    } catch (error) {}
    setIsLoading(false);
  };

  // Fetch weekly progress
  const fetchWeeklyProgress = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      let url = getApiEndpoint("/v1/recruiting/weekly-progress");
      if (selectedWeekStart) {
        url += `?week_start=${selectedWeekStart.toISOString().split("T")[0]}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const progress = result.data;

        setDashboardData(prev => ({
          ...prev,
          pipelineSnapshot: {
            lead: progress.lead,
            firstMeeting: progress.firstMeeting,
            additional: progress.additional,
            offerSent: progress.offerSent,
            signed: progress.signed,
          },
        }));
      }
    } catch (error) {}
  };

  // Save goals
  const handleSaveGoals = async goals => {
    // Optimistic update - update pipeline immediately with new goals
    setDashboardData(prev => ({
      ...prev,
      pipelineSnapshot: {
        lead: { ...prev.pipelineSnapshot.lead, goal: goals.goal_lead },
        firstMeeting: {
          ...prev.pipelineSnapshot.firstMeeting,
          goal: goals.goal_first_meeting,
        },
        additional: {
          ...prev.pipelineSnapshot.additional,
          goal: goals.goal_additional_meetings,
        },
        offerSent: {
          ...prev.pipelineSnapshot.offerSent,
          goal: goals.goal_offer_sent,
        },
        signed: { ...prev.pipelineSnapshot.signed, goal: goals.goal_signed },
      },
    }));

    setUserGoals(goals);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Save to backend
      const response = await fetch(getApiEndpoint("/v1/recruiting/goals"), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(goals),
      });

      if (response.ok) {
        // Only fetch weekly progress - no need to refetch goals we just set
        await fetchWeeklyProgress();
      }
    } catch (error) {
      // On error, refetch to get correct state
      await fetchUserGoals();
      await fetchWeeklyProgress();
    }
  };

  // Fetch scheduled meetings - OPTIMIZED: Use backend filtering instead of client-side
  const fetchScheduledMeetings = async () => {
    setIsMeetingsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Primary source: Optimized backend endpoint (already filters reported meetings)
      const response = await fetch(
        getApiEndpoint("/v1/recruiting/upcoming-meetings"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const meetings = (result.data || []).map(meeting => ({
          id: `local-${meeting.id}`,
          leadId: meeting.lead_id,
          recruitName: meeting.recruitName,
          time: new Date(meeting.time),
          summary: `Meeting with ${meeting.recruitName}`,
          source: "local",
        }));

        setUpcomingMeetings(meetings);
      } else {
        setUpcomingMeetings([]);
      }
    } catch (error) {
      // Silent fail - no meetings available
      setUpcomingMeetings([]);
    } finally {
      setIsMeetingsLoading(false);
    }
  };

  // Fetch recruiting lists
  const fetchRecruitingLists = async () => {
    setIsListsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v2/recruiting/lists"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRecruitingLists(result.data || []);
      }
    } catch (error) {
    } finally {
      setIsListsLoading(false);
    }
  };

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Fetch recruiting leads
      const response = await fetch(getApiEndpoint("/v1/recruiting/leads"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const leads = result.data || [];

        // Calculate dashboard metrics
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // Needs Attention
        const noContact7Days = leads.filter(lead => {
          if (!lead.last_contact_date) return false;
          return new Date(lead.last_contact_date) < sevenDaysAgo;
        }).length;

        const offersPendingSignup = leads.filter(
          lead => lead.status === "Offers Sent"
        ).length;

        // Count by status
        const signedCount = leads.filter(
          lead => lead.status === "Signed"
        ).length;

        const totalLeads = leads.length;

        // Store leads for modal
        setAvailableLeads(leads);

        // Insights
        const conversionRate =
          totalLeads > 0 ? Math.round((signedCount / totalLeads) * 100) : 0;

        // Recruiting Pace - average recruits signed per week
        let recruitingPace = 0;
        if (leads.length > 0 && signedCount > 0) {
          // Find the earliest lead created date
          const earliestLead = leads.reduce((earliest, lead) => {
            if (!lead.created_at) return earliest;
            const leadDate = new Date(lead.created_at);
            return !earliest || leadDate < earliest ? leadDate : earliest;
          }, null);

          if (earliestLead) {
            // Calculate weeks since first lead
            const weeksSinceStart =
              (now - earliestLead) / (1000 * 60 * 60 * 24 * 7);
            // Calculate average recruits per week
            if (weeksSinceStart > 0) {
              recruitingPace = (signedCount / weeksSinceStart).toFixed(1);
            }
          }
        }

        setDashboardData(prev => ({
          ...prev,
          needsAttention: {
            noContact7Days,
            offersPendingSignup,
          },
          upcomingMeetings: [],
          insights: {
            conversionRate,
            avgDaysPerStage: recruitingPace,
            totalPipeline: totalLeads,
          },
        }));
      }
    } catch (error) {}
  };

  useEffect(() => {
    const initializeDashboard = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchUserGoals(),
        fetchWeeklyProgress(),
        fetchDashboardData(),
        fetchRecruitingLists(),
        fetchScheduledMeetings(),
      ]);
      setIsLoading(false);
    };

    initializeDashboard();

    // // Auto-refresh meetings every 30 seconds (both Google Calendar and local DB)
    // const meetingsInterval = setInterval(() => {
    //   fetchScheduledMeetings();
    // }, 30000);

    // return () => clearInterval(meetingsInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeekStart]);

  // Helper functions for week navigation
  const getWeekStart = (date = new Date()) => {
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };

  const getWeekEnd = weekStart => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return weekEnd;
  };

  const formatWeekRange = weekStart => {
    if (!weekStart) {
      weekStart = getWeekStart();
    }
    const weekEnd = getWeekEnd(weekStart);
    return `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };

  const handlePreviousWeek = () => {
    const currentWeek = selectedWeekStart || getWeekStart();
    const prevWeek = new Date(currentWeek);
    prevWeek.setDate(currentWeek.getDate() - 7);
    setSelectedWeekStart(prevWeek);
  };

  const handleNextWeek = () => {
    const currentWeek = selectedWeekStart || getWeekStart();
    const nextWeek = new Date(currentWeek);
    nextWeek.setDate(currentWeek.getDate() + 7);
    const thisWeek = getWeekStart();

    // Don't allow navigating beyond current week
    if (nextWeek <= thisWeek) {
      setSelectedWeekStart(nextWeek >= thisWeek ? null : nextWeek);
    }
  };

  const isCurrentWeek = () => {
    if (!selectedWeekStart) return true;
    const thisWeek = getWeekStart();
    return selectedWeekStart.getTime() === thisWeek.getTime();
  };

  const handleViewAllReps = () => {
    navigate("/recruiting/pipeline", {
      state: { preloadedLeads: availableLeads },
    });
  };

  const handleLeadAdded = () => {
    // Refresh dashboard data to update metrics
    fetchDashboardData();
    fetchWeeklyProgress();
  };

  const handleListCreated = newList => {
    // Add the new list to the state with lead_count
    setRecruitingLists(prev => [
      {
        ...newList,
        lead_count: newList.lead_count || 0,
      },
      ...prev,
    ]);
  };

  const handleEditList = list => {
    setListToEdit(list);
    setShowEditListModal(true);
  };

  const handleListUpdated = updatedList => {
    // Update the list in the state
    setRecruitingLists(prev =>
      prev.map(list =>
        list.id === updatedList.id
          ? {
              ...updatedList,
              lead_count: updatedList.lead_count || 0,
            }
          : list
      )
    );

    // Close modal and reset state
    setShowEditListModal(false);
    setListToEdit(null);
  };

  const handleDeleteList = list => {
    setListToDelete(list);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!listToDelete) return;

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/recruiting/lists/${listToDelete.id}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        // Remove the list from state
        setRecruitingLists(prev =>
          prev.filter(list => list.id !== listToDelete.id)
        );

        // Close modal and reset state
        setShowDeleteModal(false);
        setListToDelete(null);
      } else {
        showError("Error deleting list");
      }
    } catch (error) {
      handleGenericError(error, "Error deleting list");
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setListToDelete(null);
  };

  const handleCloseReportModal = async (reportSaved, reportData) => {
    setShowReportModal(false);
    if (reportSaved) {
      // Refresh dashboard data to update meetings and pipeline
      await fetchDashboardData();
      await fetchScheduledMeetings();
      await fetchWeeklyProgress();
    }
    setSelectedRecruitForReport(null);
    setSelectedMeetingForReport(null);
  };

  const handleScheduleMeeting = (recruit, shouldAdvanceAfter = false) => {
    // Navigate to pipeline page for scheduling meetings
    navigate("/recruiting/pipeline", {
      state: {
        preloadedLeads: availableLeads,
        openSchedulerModal: true,
        selectedRecruit: recruit,
      },
    });
  };

  const handleAdvanceStage = async recruit => {
    // Refresh data after stage advancement
    await fetchDashboardData();
    await fetchWeeklyProgress();
  };

  const handleDocumentSent = async recruit => {
    // Refresh data after document sent
    await fetchDashboardData();
    await fetchWeeklyProgress();
  };

  const recruitingTabs = [
    { label: "Dashboard", path: "/recruiting" },
    { label: "Pipeline", path: "/recruiting/pipeline" },
    { label: "Lists", path: "/recruiting/list" },
    { label: "Documents", path: "/recruiting/documents" },
  ];

  return (
    <PageContent
      customHeader={
        <div className="recruiting-page-custom-header">
          <div className="recruiting-page-header-left">
            <h1 className="recruiting-page-main-title">Recruiting</h1>
            <div className="recruiting-page-nav-tabs">
              {recruitingTabs.map(tab => (
                <button
                  key={tab.path}
                  className={`recruiting-page-nav-tab ${
                    location.pathname === tab.path ? "active" : ""
                  }`}
                  onClick={() => navigate(tab.path)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      }
    >
      <div className="recruiting-dashboard">
        {/* Main Content Grid */}
        <div className="dashboard-top-section">
          {/* Left Column - Needs Attention and Cards */}
          <div className="needs-attention-wrapper">
            {/* Needs Attention Cards */}
            {/* COMMENTED OUT - No Contact and Offers Pending Signups cards */}
            {/* <div className="needs-attention-grid">
              <div className="attention-card attention-card-red">
                <div className="attention-card-icon">
                  <span className="material-icons">contact_mail</span>
                </div>
                <div className="attention-card-content">
                  <div className="attention-card-count">
                    {dashboardData.needsAttention.noContact7Days}
                  </div>
                  <span className="attention-card-label">
                    No Contact 7+ days
                  </span>
                </div>
              </div>

              <div className="attention-card attention-card-blue">
                <div className="attention-card-icon">
                  <span className="material-icons">pending_actions</span>
                </div>
                <div className="attention-card-content">
                  <div className="attention-card-count">
                    {dashboardData.needsAttention.offersPendingSignup}
                  </div>
                  <span className="attention-card-label">
                    Offers pending signups
                  </span>
                </div>
              </div>
            </div> */}

            {/* Pipeline Snapshot Card */}
            <div className="dashboard-card pipeline-snapshot-card">
              <div className="card-header">
                <span className="material-icons card-icon">bar_chart</span>
                <h3 className="card-title">Pipeline Snapshot</h3>
                <button
                  className="edit-goals-btn"
                  onClick={() => setShowGoalsModal(true)}
                  disabled={!isCurrentWeek()}
                  title={
                    !isCurrentWeek()
                      ? "Return to current week to edit goals"
                      : ""
                  }
                >
                  <span className="material-icons">edit</span>
                  <span>Edit Goals</span>
                </button>
                <button
                  className="view-all-reps-header-btn"
                  onClick={handleViewAllReps}
                >
                  <span className="material-icons">visibility</span>
                  <span>View Pipeline</span>
                </button>
              </div>
              <div className="week-navigation">
                <button className="week-nav-btn" onClick={handlePreviousWeek}>
                  <span className="material-icons">chevron_left</span>
                </button>
                <span className="week-range-label">
                  {isCurrentWeek()
                    ? "This Week"
                    : formatWeekRange(selectedWeekStart)}
                </span>
                <button
                  className="week-nav-btn"
                  onClick={handleNextWeek}
                  disabled={isCurrentWeek()}
                >
                  <span className="material-icons">chevron_right</span>
                </button>
              </div>
              <div className="pipeline-items">
                {isLoading ? (
                  <>
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="pipeline-item">
                        <div className="pipeline-item-header">
                          <span
                            className="pipeline-label skeleton-text"
                            style={{ width: "120px", height: "14px" }}
                          ></span>
                          <span
                            className="pipeline-count skeleton-text"
                            style={{ width: "50px", height: "14px" }}
                          ></span>
                        </div>
                        <div className="pipeline-bar">
                          <div className="pipeline-bar-skeleton"></div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="pipeline-item">
                      <div className="pipeline-item-header">
                        <span className="pipeline-label">Contacted</span>
                        <span className="pipeline-count">
                          {dashboardData.pipelineSnapshot.lead.count} /{" "}
                          {dashboardData.pipelineSnapshot.lead.goal}
                        </span>
                      </div>
                      <div className="pipeline-bar">
                        <div
                          className="pipeline-bar-fill pipeline-orange"
                          style={{
                            width:
                              dashboardData.pipelineSnapshot.lead.goal > 0
                                ? `${Math.min((dashboardData.pipelineSnapshot.lead.count / dashboardData.pipelineSnapshot.lead.goal) * 100, 100)}%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="pipeline-item">
                      <div className="pipeline-item-header">
                        <span className="pipeline-label">
                          1st Meeting Occurred
                        </span>
                        <span className="pipeline-count">
                          {dashboardData.pipelineSnapshot.firstMeeting.count} /{" "}
                          {dashboardData.pipelineSnapshot.firstMeeting.goal}
                        </span>
                      </div>
                      <div className="pipeline-bar">
                        <div
                          className="pipeline-bar-fill pipeline-orange"
                          style={{
                            width:
                              dashboardData.pipelineSnapshot.firstMeeting.goal >
                              0
                                ? `${Math.min((dashboardData.pipelineSnapshot.firstMeeting.count / dashboardData.pipelineSnapshot.firstMeeting.goal) * 100, 100)}%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="pipeline-item">
                      <div className="pipeline-item-header">
                        <span className="pipeline-label">
                          2nd Meeting Occurred
                        </span>
                        <span className="pipeline-count">
                          {dashboardData.pipelineSnapshot.additional.count} /{" "}
                          {dashboardData.pipelineSnapshot.additional.goal}
                        </span>
                      </div>
                      <div className="pipeline-bar">
                        <div
                          className="pipeline-bar-fill pipeline-orange"
                          style={{
                            width:
                              dashboardData.pipelineSnapshot.additional.goal > 0
                                ? `${Math.min((dashboardData.pipelineSnapshot.additional.count / dashboardData.pipelineSnapshot.additional.goal) * 100, 100)}%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="pipeline-item">
                      <div className="pipeline-item-header">
                        <span className="pipeline-label">Offer Sent</span>
                        <span className="pipeline-count">
                          {dashboardData.pipelineSnapshot.offerSent.count} /{" "}
                          {dashboardData.pipelineSnapshot.offerSent.goal}
                        </span>
                      </div>
                      <div className="pipeline-bar">
                        <div
                          className="pipeline-bar-fill pipeline-orange"
                          style={{
                            width:
                              dashboardData.pipelineSnapshot.offerSent.goal > 0
                                ? `${Math.min((dashboardData.pipelineSnapshot.offerSent.count / dashboardData.pipelineSnapshot.offerSent.goal) * 100, 100)}%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="pipeline-item">
                      <div className="pipeline-item-header">
                        <span className="pipeline-label">Signed</span>
                        <span className="pipeline-count">
                          {dashboardData.pipelineSnapshot.signed.count} /{" "}
                          {dashboardData.pipelineSnapshot.signed.goal}
                        </span>
                      </div>
                      <div className="pipeline-bar">
                        <div
                          className="pipeline-bar-fill pipeline-orange"
                          style={{
                            width:
                              dashboardData.pipelineSnapshot.signed.goal > 0
                                ? `${Math.min((dashboardData.pipelineSnapshot.signed.count / dashboardData.pipelineSnapshot.signed.goal) * 100, 100)}%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="dashboard-right-column">
            {/* Upcoming Meetings Calendar Card */}
            <div
              className="dashboard-card calendar-card"
              style={{ height: "100%" }}
            >
              <div className="card-header">
                <span className="material-icons card-icon">event</span>
                <h3 className="card-title">Meetings</h3>
                <button
                  className="view-all-calendar-btn"
                  onClick={handleViewAllReps}
                  style={{
                    padding: "6px 12px",
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    cursor: "pointer",
                    color: "#6b7280",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.color = "#374151";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#6b7280";
                  }}
                >
                  <span className="material-icons" style={{ fontSize: "16px" }}>
                    visibility
                  </span>
                  <span>View All</span>
                </button>
              </div>
              <div
                className="meetings-list"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "12px 0",
                  flex: 1,
                  overflowY: "auto",
                  minHeight: 0,
                }}
              >
                {isMeetingsLoading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#6b7280",
                    }}
                  >
                    <span
                      className="material-icons rotating-icon"
                      style={{ fontSize: "48px", color: "#3b82f6" }}
                    >
                      refresh
                    </span>
                    <p style={{ marginTop: "1rem", fontSize: "0.875rem" }}>
                      Loading meetings...
                    </p>
                  </div>
                ) : upcomingMeetings.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#6b7280",
                    }}
                  >
                    <span
                      className="material-icons"
                      style={{ fontSize: "48px", color: "#d1d5db" }}
                    >
                      event_available
                    </span>
                    <p style={{ marginTop: "1rem", fontSize: "0.875rem" }}>
                      No meetings scheduled
                    </p>
                  </div>
                ) : (
                  upcomingMeetings.map(meeting => {
                    const now = new Date();
                    const isToday =
                      meeting.time.toDateString() === now.toDateString();
                    const isTomorrow =
                      meeting.time.toDateString() ===
                      new Date(Date.now() + 86400000).toDateString();
                    const isPast = meeting.time < now;

                    return (
                      <div
                        key={meeting.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px",
                          backgroundColor: isPast
                            ? "#fee2e2"
                            : isToday
                              ? "#fef3c7"
                              : "#f9fafb",
                          borderRadius: "8px",
                          borderLeft: `4px solid ${isPast ? "#dc2626" : isToday ? "#f59e0b" : "#3b82f6"}`,
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onClick={() =>
                          navigate("/recruiting/pipeline", {
                            state: { preloadedLeads: availableLeads },
                          })
                        }
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = isPast
                            ? "#fecaca"
                            : isToday
                              ? "#fde68a"
                              : "#f3f4f6";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = isPast
                            ? "#fee2e2"
                            : isToday
                              ? "#fef3c7"
                              : "#f9fafb";
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minWidth: "60px",
                            padding: "8px",
                            backgroundColor: "white",
                            borderRadius: "6px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#6b7280",
                              fontWeight: "600",
                              textTransform: "uppercase",
                            }}
                          >
                            {meeting.time.toLocaleDateString("en-US", {
                              month: "short",
                            })}
                          </div>
                          <div
                            style={{
                              fontSize: "1.5rem",
                              fontWeight: "700",
                              color: "#111827",
                            }}
                          >
                            {meeting.time.getDate()}
                          </div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: "600",
                              color: "#111827",
                              fontSize: "0.9rem",
                            }}
                          >
                            {meeting.recruitName}
                          </div>
                          <div
                            style={{
                              fontSize: "0.875rem",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            {meeting.time.toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })}
                            {isPast && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  padding: "2px 8px",
                                  backgroundColor: "#dc2626",
                                  color: "white",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: "600",
                                }}
                              >
                                Past
                              </span>
                            )}
                            {isToday && !isPast && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  padding: "2px 8px",
                                  backgroundColor: "#f59e0b",
                                  color: "white",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: "600",
                                }}
                              >
                                Today
                              </span>
                            )}
                            {isTomorrow && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  padding: "2px 8px",
                                  backgroundColor: "#3b82f6",
                                  color: "white",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: "600",
                                }}
                              >
                                Tomorrow
                              </span>
                            )}
                          </div>
                        </div>
                        {isPast ? (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              // Find the lead for this meeting and open report modal
                              const lead = availableLeads.find(
                                l => l.id === meeting.leadId
                              );
                              if (lead) {
                                setSelectedRecruitForReport(lead);
                                setSelectedMeetingForReport({
                                  time: meeting.time,
                                  duration: 30,
                                });
                                setShowReportModal(true);
                              }
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "6px 12px",
                              backgroundColor: "#dc2626",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "0.875rem",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = "#b91c1c";
                              e.currentTarget.style.transform = "scale(1.05)";
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = "#dc2626";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "16px" }}
                            >
                              assignment
                            </span>
                            <span>Report</span>
                          </button>
                        ) : (
                          <span
                            className="material-icons"
                            style={{ color: "#9ca3af", fontSize: "20px" }}
                          >
                            chevron_right
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* COMMENTED OUT - Insights Card */}
            {/* <div className="dashboard-card insights-card">
              <div className="card-header">
                <span className="material-icons card-icon">lightbulb</span>
                <h3 className="card-title">Insights</h3>
              </div>
              <div className="insights-grid">
                <div className="insight-item">
                  <div className="insight-icon insight-green">
                    <span className="material-icons">trending_up</span>
                  </div>
                  <div className="insight-value">
                    {dashboardData.insights.conversionRate}%
                  </div>
                  <div className="insight-label">Conversion Rate</div>
                  <div className="insight-sublabel">Lead → Signed</div>
                </div>
                <div className="insight-item">
                  <div className="insight-icon insight-purple">
                    <span className="material-icons">people</span>
                  </div>
                  <div className="insight-value">
                    {dashboardData.insights.totalPipeline}
                  </div>
                  <div className="insight-label">Total Pipeline</div>
                  <div className="insight-sublabel">All active recruits</div>
                </div>
                <div className="insight-item">
                  <div className="insight-icon insight-blue">
                    <span className="material-icons">speed</span>
                  </div>
                  <div className="insight-value">
                    {dashboardData.insights.avgDaysPerStage}
                  </div>
                  <div className="insight-label">Recruiting Pace</div>
                  <div className="insight-sublabel">Recruits per week</div>
                </div>
              </div>
            </div> */}

            {/* COMMENTED OUT - Quick Actions Card */}
            {/* <div className="dashboard-card quick-actions-card">
              <div className="card-header">
                <span className="material-icons card-icon">bolt</span>
                <h3 className="card-title">Quick Actions</h3>
              </div>
              <div className="actions-list">
                <button className="action-item" onClick={handleAddRecruit}>
                  <span className="material-icons">add_circle</span>
                  <span>Add Recruit</span>
                </button>
                <button className="action-item">
                  <span className="material-icons">phone</span>
                  <span>Log Contact</span>
                </button>
                <button className="action-item">
                  <span className="material-icons">event</span>
                  <span>Schedule Meeting</span>
                </button>
                <button className="action-item">
                  <span className="material-icons">send</span>
                  <span>Send Offer</span>
                </button>
                <button className="action-item">
                  <span className="material-icons">ac_unit</span>
                  <span>Mark as Cold</span>
                </button>
              </div>
            </div> */}
          </div>
        </div>

        {/* Recruiting Lists Section */}
        <div className="recruiting-lists-section">
          <div className="dashboard-card">
            <div className="card-header">
              <span className="material-icons card-icon">list_alt</span>
              <h3 className="card-title">My Recruiting Lists</h3>
              <button
                className="create-list-btn"
                onClick={() => setShowCreateListModal(true)}
              >
                <span className="material-icons">add</span>
                <span>Create New List</span>
              </button>
            </div>
            <div className="lists-grid">
              {isListsLoading ? (
                <div className="loading-lists-state">
                  <div className="loading-spinner">
                    <span className="material-icons rotating-icon">
                      refresh
                    </span>
                  </div>
                  <p>Loading recruiting lists...</p>
                </div>
              ) : recruitingLists.length === 0 ? (
                <div className="empty-lists-state">
                  <span className="material-icons">folder_open</span>
                  <p>No lists created yet</p>
                  <button
                    className="create-first-list-btn"
                    onClick={() => setShowCreateListModal(true)}
                  >
                    Create Your First List
                  </button>
                </div>
              ) : (
                recruitingLists.map(list => (
                  <div
                    key={list.id}
                    className="list-card"
                    onClick={() => {
                      // Check if this is the Cold Leads list
                      if (list.name.toLowerCase() === "cold leads") {
                        navigate("/recruiting/cold");
                      } else {
                        // Navigate to the new card-based list view
                        navigate(
                          `/recruiting/list?listId=${list.id}&listName=${encodeURIComponent(list.name)}`
                        );
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="list-card-header">
                      <div className="list-icon">
                        <span className="material-icons">folder</span>
                      </div>
                      <h4 className="list-name">{list.name}</h4>
                    </div>
                    <div className="list-card-body">
                      <div className="list-stat">
                        <span className="list-count">
                          {list.lead_count || 0}
                        </span>
                        <span className="list-label">Recruits</span>
                      </div>
                    </div>
                    <div className="list-card-footer">
                      <span className="list-updated">
                        {list.is_default ? (
                          <span className="default-list-badge">Default</span>
                        ) : list.is_system_list ? (
                          <span className="default-list-badge">Default</span>
                        ) : (
                          `Updated ${new Date(list.updated_at).toLocaleDateString()}`
                        )}
                      </span>
                      <div className="list-actions">
                        {!list.is_default && !list.is_system_list && (
                          <>
                            <button
                              className="list-action-btn"
                              title="Edit List"
                              onClick={e => {
                                e.stopPropagation();
                                handleEditList(list);
                              }}
                            >
                              <span className="material-icons">edit</span>
                            </button>
                            <button
                              className="list-action-btn delete-btn"
                              title="Delete List"
                              onClick={e => {
                                e.stopPropagation();
                                handleDeleteList(list);
                              }}
                            >
                              <span className="material-icons">delete</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <SetGoalsModal
        isOpen={showGoalsModal}
        onClose={() => setShowGoalsModal(false)}
        onSave={handleSaveGoals}
        currentGoals={userGoals}
      />

      <CreateListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onListCreated={handleListCreated}
        availableLeads={availableLeads}
      />

      <EditListModal
        isOpen={showEditListModal}
        onClose={() => {
          setShowEditListModal(false);
          setListToEdit(null);
        }}
        onListUpdated={handleListUpdated}
        availableLeads={availableLeads}
        listToEdit={listToEdit}
      />

      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onLeadAdded={handleLeadAdded}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        itemName={listToDelete?.name}
        itemType="list"
      />

      <MeetingReportModal
        isOpen={showReportModal}
        onClose={handleCloseReportModal}
        recruit={selectedRecruitForReport}
        meeting={selectedMeetingForReport}
        onScheduleNewMeeting={handleScheduleMeeting}
        onAdvanceStage={handleAdvanceStage}
        onDocumentSent={handleDocumentSent}
      />
    </PageContent>
  );
}

export default RecruitingDashboard;
